/**
 * useDismissedPatterns — per-surface FORGE pattern dismiss state with
 * composite-key localStorage persistence.
 *
 * Cycle-102 sprint-3 Task 3.3 (sprint plan §451 + SDD §1.9 + R-SDD-4 +
 * PRD §5.6 row 4). The composite key
 *
 *     dismissed:{operator_id}:{surface}:{pattern_id}
 *
 * means dismissals do NOT cross surfaces — operator dismisses pattern X
 * in Workspace, X stays visible in Environment. The key also scopes per
 * operator so a future multi-operator deployment doesn't have one user's
 * dismissals shadowing another's.
 *
 * The hook is storage-event aware so a dismiss on one surface is
 * reflected immediately if the same surface is rendered in another tab.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { getCurrentOperatorId } from '../lib/operatorId';

export type DismissSurface = 'workspace' | 'environment';

interface UseDismissedPatternsOptions {
  surface: DismissSurface;
  /**
   * Override the operator id (test convenience). Production paths leave
   * this undefined and let the helper read from the JWT.
   */
  operatorId?: string;
}

function dismissKey(operatorId: string, surface: DismissSurface, patternId: string): string {
  return `dismissed:${operatorId}:${surface}:${patternId}`;
}

const KEY_PREFIX = 'dismissed:';

function readSurfaceSet(operatorId: string, surface: DismissSurface): Set<string> {
  const out = new Set<string>();
  if (typeof localStorage === 'undefined') return out;
  const prefix = `${KEY_PREFIX}${operatorId}:${surface}:`;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        out.add(key.slice(prefix.length));
      }
    }
  } catch {
    // private-browsing / quota errors → degrade to in-memory only.
  }
  return out;
}

export interface UseDismissedPatternsResult {
  dismissed: ReadonlySet<string>;
  dismiss: (patternId: string) => void;
  restore: (patternId?: string) => void;
  isDismissed: (patternId: string) => boolean;
}

export function useDismissedPatterns(
  options: UseDismissedPatternsOptions,
): UseDismissedPatternsResult {
  const operatorId = options.operatorId ?? getCurrentOperatorId();
  const surface = options.surface;

  const [dismissed, setDismissed] = useState<Set<string>>(
    () => readSurfaceSet(operatorId, surface),
  );

  // Listen for storage events so a dismiss in another tab on the same
  // surface propagates here. Without this, two tabs of the same
  // surface drift apart on dismiss.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const prefix = `${KEY_PREFIX}${operatorId}:${surface}:`;
    function onStorage(e: StorageEvent) {
      if (!e.key || !e.key.startsWith(prefix)) return;
      setDismissed(readSurfaceSet(operatorId, surface));
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [operatorId, surface]);

  const dismiss = useCallback(
    (patternId: string) => {
      try {
        localStorage.setItem(dismissKey(operatorId, surface, patternId), '1');
      } catch {
        /* in-memory only fallback */
      }
      setDismissed((prev) => {
        if (prev.has(patternId)) return prev;
        const next = new Set(prev);
        next.add(patternId);
        return next;
      });
    },
    [operatorId, surface],
  );

  const restore = useCallback(
    (patternId?: string) => {
      if (patternId === undefined) {
        // Restore all on this surface.
        setDismissed((prev) => {
          if (prev.size === 0) return prev;
          for (const id of prev) {
            try {
              localStorage.removeItem(dismissKey(operatorId, surface, id));
            } catch {
              /* ignore */
            }
          }
          return new Set();
        });
        return;
      }
      try {
        localStorage.removeItem(dismissKey(operatorId, surface, patternId));
      } catch {
        /* ignore */
      }
      setDismissed((prev) => {
        if (!prev.has(patternId)) return prev;
        const next = new Set(prev);
        next.delete(patternId);
        return next;
      });
    },
    [operatorId, surface],
  );

  const isDismissed = useCallback(
    (patternId: string) => dismissed.has(patternId),
    [dismissed],
  );

  return useMemo(
    () => ({ dismissed, dismiss, restore, isDismissed }),
    [dismissed, dismiss, restore, isDismissed],
  );
}
